import { useConvex } from "convex/react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useEditorStore } from "@/store/editorStore";
import { useTrashedPanoramas, restorePanorama as restoreLocalPanorama, emptyTrash as emptyLocalTrash } from "@/data/adapters/local/panoramas";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import {
  emptyProjectTrash,
  listProjectTrashWithLegacy,
  migrateLegacyTrash,
  restorePanoramaFromTrash,
  type TrashMetadata,
} from "@/utils/trashManager";
import { api } from "../../convex/_generated/api";

interface TrashItem {
  name: string;
  path: string;
  fileCount: number;
  metadata?: TrashMetadata | null;
  isLegacy: boolean;
}

export default function TrashPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const convex = useConvex();
  const { project } = useEditorStore();
  const projectBackend = useEditorStore((state) => state.projectBackend);
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const useLocalBackend = isElectron() && projectBackend === "local";
  const localTrashedPanoramas = useTrashedPanoramas(useLocalBackend ? projectId : undefined, useLocalBackend ? (project?.craftspace_id || "default") : undefined);

  const craftspaceId = project?.craftspace_id || "default";
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId,
        projectId: project.id,
        projectName: project.name,
        projectData,
        userEmail: user?.email,
        userId: user?.id,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: craftspaceId, projectKey: projectId || "default" };

  console.log("🔍 Trash page debug info:", {
    projectId,
    project,
    craftspaceId,
    user: user?.id,
  });

  const refresh = useCallback(async () => {
    if (!projectId) return;
    if (useLocalBackend) return;
    setLoading(true);
    try {
      const data = await listProjectTrashWithLegacy(
        craftspaceId,
        projectId,
        storageSegments,
      );
      setItems(data);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast({ variant: "destructive", title: "Error", description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [projectId, craftspaceId, storageSegments, toast, useLocalBackend]);

  const handleMigrateLegacy = async () => {
    if (!projectId) return;
    setMigrating(true);
    try {
      const result = await migrateLegacyTrash(craftspaceId, projectId, storageSegments);
      if (result.migrated > 0) {
        toast({
          title: "Migration complete",
          description: `Migrated ${result.migrated} legacy trash items to new structure.`,
        });
        refresh(); // Refresh to show updated list
      } else {
        toast({ title: "No legacy items", description: "No legacy trash items found to migrate." });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast({ variant: "destructive", title: "Error", description: errorMessage });
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    if (useLocalBackend) {
      const mapped =
        localTrashedPanoramas?.map((p) => ({
          name: p.name,
          path: p.id,
          fileCount: 1,
          metadata: p.deleted_at ? { originalPath: "", movedAt: p.deleted_at } : null,
          isLegacy: false,
        })) ?? [];
      setItems(mapped);
      return;
    }
    refresh();
  }, [refresh, useLocalBackend, localTrashedPanoramas]);

  const hasLegacyItems = !useLocalBackend && items.some((item) => item.isLegacy);

  return (
    <div className="flex flex-col h-full bg-gray-800">
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto py-8 px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Trash</h1>
              <p className="text-gray-300">Manage deleted project files</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  console.log("🔍 Debug: Current state:", {
                    projectId,
                    project,
                    craftspaceId,
                    user: user?.id,
                  });

                  // Test storage paths
                  console.log("📁 Test storage paths:", {
                    trashRoot: StoragePaths.private.trash.root(
                      storageSegments.craftspaceKey,
                      storageSegments.projectKey,
                    ),
                    panoramaPath: StoragePaths.private.trash.panorama(
                      storageSegments.craftspaceKey,
                      storageSegments.projectKey,
                      "test",
                      "test.jpg",
                    ),
                    metadataPath: StoragePaths.private.trash.metadata(
                      storageSegments.craftspaceKey,
                      storageSegments.projectKey,
                      "test",
                    ),
                  });
                }}
                className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
              >
                Debug Paths
              </Button>
              {hasLegacyItems && (
                <Button
                  variant="outline"
                  onClick={handleMigrateLegacy}
                  disabled={migrating}
                  className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/20"
                >
                  {migrating ? "Migrating..." : "Migrate Legacy Items"}
                </Button>
              )}
              <Button
                variant="destructive"
                onClick={async () => {
                  try {
                    if (!projectId) {
                      toast({
                        variant: "destructive",
                        title: "Project not found",
                        description: "Unable to empty trash without a project.",
                      });
                      return;
                    }
                    if (useLocalBackend) {
                      await emptyLocalTrash(projectId, craftspaceId);
                    } else {
                      await emptyProjectTrash(craftspaceId, projectId, storageSegments);
                    }
                    toast({ title: "Trash emptied" });
                    refresh();
                  } catch (error: unknown) {
                    const errorMessage =
                      error instanceof Error ? error.message : "Unknown error occurred";
                    toast({ variant: "destructive", title: "Error", description: errorMessage });
                  }
                }}
              >
                Empty Trash
              </Button>
            </div>
          </div>

          {hasLegacyItems && (
            <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
              <div className="text-yellow-300 text-sm">
                <strong>Legacy trash items detected:</strong> Some items are stored in the old
                craftspace-level structure. Use the "Migrate Legacy Items" button to move them to the
                new project-local structure for better organization.
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-gray-300 text-center py-8">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">Trash is empty</div>
              <div className="text-gray-500 text-sm">
                Deleted files will appear here for easy restoration
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => (
                <div
                  key={it.path}
                  className={`p-4 rounded-lg flex items-center justify-between ${
                    it.isLegacy
                      ? "bg-yellow-900/20 border border-yellow-600/30"
                      : "bg-gray-800 border border-gray-700"
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${it.isLegacy ? "text-yellow-300" : "text-white"}`}
                      >
                        {it.name}
                      </span>
                      {it.isLegacy && (
                        <span className="text-xs bg-yellow-600 text-yellow-100 px-2 py-1 rounded">
                          Legacy
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{it.fileCount} files</div>
                    {it.metadata?.movedAt && (
                      <div className="text-xs text-gray-500">
                        Moved: {new Date(it.metadata.movedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!it.isLegacy && (
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            if (!projectId) {
                              toast({
                                variant: "destructive",
                                title: "Project not found",
                                description: "Unable to restore without a project.",
                              });
                              return;
                            }
                            const targetProjectId = projectId;
                            if (useLocalBackend) {
                              await restoreLocalPanorama(it.path, craftspaceId);
                              toast({ title: "Panorama restored" });
                            } else {
                              await restorePanoramaFromTrash(
                                craftspaceId,
                                targetProjectId,
                                it.name,
                                storageSegments,
                              );
                              toast({ title: "Panorama restored" });

                              // Use storage discovery to find the restored panorama
                              const { discoverPanoramasFromStorage, syncDiscoveredPanoramas } =
                                useEditorStore.getState();
                              await discoverPanoramasFromStorage(
                                storageSegments.craftspaceKey,
                                storageSegments.projectKey,
                              );

                              // Sync discovered panoramas with database
                              await syncDiscoveredPanoramas(
                                storageSegments.craftspaceKey,
                                storageSegments.projectKey,
                              );

                              // Directly fetch the updated panorama list from database and update store
                              try {
                                const updatedPanoramas = await convex.query(
                                  api.panoramas.listByProject,
                                  { projectId },
                                );

                                if (updatedPanoramas) {
                                  // Update the store directly with the fresh data
                                  const { setPanoramas } = useEditorStore.getState();
                                  setPanoramas(updatedPanoramas);
                                  console.log(
                                    "✅ Updated panorama list in store:",
                                    updatedPanoramas.length,
                                    "panoramas",
                                  );
                                }
                              } catch (dbError) {
                                console.warn("Failed to fetch updated panorama list:", dbError);
                              }
                            }

                            // Force a refresh of the main editor by dispatching a custom event
                            window.dispatchEvent(new CustomEvent("editor-refresh-needed"));

                            // Also refresh the trash list
                            refresh();

                            toast({
                              title: "Panorama list updated",
                              description:
                                "The restored panorama should now appear in the Hotspot Editor",
                            });
                          } catch (error: unknown) {
                            const errorMessage =
                              error instanceof Error ? error.message : "Unknown error occurred";
                            toast({
                              variant: "destructive",
                              title: "Error",
                              description: errorMessage,
                            });
                          }
                        }}
                      >
                        Restore
                      </Button>
                    )}
                    {it.isLegacy && (
                      <span className="text-xs text-yellow-400 italic">
                        Use Migrate button above
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
